/**
 * Resuelve la configuración de presentación del tenant de la sesión.
 *
 * Nunca bloquea el render: mientras carga se usan los defaults. Un dashboard
 * que muestra una pantalla en blanco esperando un JSON de 200 bytes se
 * percibe como roto, y el dato que falta sólo cambia cómo se ven las comas.
 *
 * Lo que sí se publica es **si el dato ya se resolvió** (`isResolved`), para
 * que el idioma no parpadee en el arranque. Ver `useTenantSettings.ts`.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';

import { tenantKeys } from '@/api/keys';
import { useAuth } from '@/auth/useAuth';
import { DEFAULT_TENANT_SETTINGS, fetchTenantSettings } from './settings';
import { TenantContext, type TenantContextValue } from './useTenantSettings';

export function TenantProvider({
  children,
  fetchImpl,
}: {
  children: ReactNode;
  /**
   * El `fetch` de la aplicación. No es un `ApiClient`: esto es un asset
   * estático del mismo origen, sin bearer y sin baseUrl de API. Llega por
   * prop —y no como `fetch` global— para que un test pueda montar el árbol
   * entero sin red. Ver `app/runtime.ts`.
   */
  fetchImpl?: typeof fetch;
}): ReactNode {
  const { claims } = useAuth();
  const slug = claims?.tenant ?? null;

  const { data } = useQuery({
    queryKey: tenantKeys.settings(slug ?? 'anon'),
    queryFn: ({ signal }) => fetchTenantSettings(slug ?? '', fetchImpl ?? fetch, signal),
    enabled: slug !== null,
    // Es un asset estático que cambia cuando cambia el contrato del cliente.
    // No hay motivo para volver a pedirlo durante la sesión.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  const value = useMemo<TenantContextValue>(
    () => ({ settings: data ?? DEFAULT_TENANT_SETTINGS, isResolved: data !== undefined }),
    [data],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}
